import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Save, SendHorizonal, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatDate } from '@/lib/api';

const PRESETS = [
  { label: 'Gmail', host: 'smtp.gmail.com', port: 465, security: 'ssl' },
  { label: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587, security: 'tls' },
  { label: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 465, security: 'ssl' },
  { label: 'Zoho', host: 'smtp.zoho.com', port: 465, security: 'ssl' },
  { label: 'Custom', host: '', port: 587, security: 'tls' },
];

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState({
    smtp_email: '', gmail_email: '', app_password: '', sender_name: '', email_signature: '', company_name: '',
    smtp_host: 'smtp.gmail.com', smtp_port: 465, smtp_security: 'ssl'
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get('/email-settings'), api.get('/email-logs')]);
      if (s.data && (s.data.smtp_email || s.data.gmail_email)) {
        setSettings(prev => ({
          ...prev, ...s.data,
          smtp_email: s.data.smtp_email || s.data.gmail_email || '',
          smtp_host: s.data.smtp_host || 'smtp.gmail.com',
          smtp_port: s.data.smtp_port || 465,
          smtp_security: s.data.smtp_security || 'ssl',
          app_password: ''
        }));
        setPasswordChanged(false);
      }
      setLogs(l.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!settings.smtp_email) { toast.error('Email address is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...settings,
        gmail_email: settings.smtp_email,
        app_password: passwordChanged ? settings.app_password : '********'
      };
      await api.post('/email-settings', payload);
      toast.success('Email settings saved');
      setPasswordChanged(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post('/email-settings/test');
      toast.success('Test email sent! Check your inbox.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(false);
  };

  const applyPreset = (label) => {
    const p = PRESETS.find(x => x.label === label);
    if (p) setSettings(prev => ({ ...prev, smtp_host: p.host, smtp_port: p.port, smtp_security: p.security }));
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Email Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure SMTP to send invoices and reminders directly from the app</p>
      </div>

      {/* Settings Form */}
      <Card className="bg-card border-border" data-testid="email-settings-card">
        <CardHeader>
          <CardTitle className="text-lg text-card-foreground">SMTP Configuration</CardTitle>
          <CardDescription className="text-muted-foreground">Works with Gmail, Outlook, Zoho, or any SMTP provider</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Presets */}
          <div className="space-y-2">
            <Label className="text-card-foreground/80">Quick Setup</Label>
            <div className="flex gap-2 flex-wrap">
              {PRESETS.map(p => (
                <Button key={p.label} size="sm" variant="outline"
                  className={`h-7 text-xs border-input ${settings.smtp_host === p.host ? 'bg-blue-600 text-white border-blue-600' : 'text-card-foreground/80'}`}
                  onClick={() => applyPreset(p.label)} data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {/* SMTP Server */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-card-foreground/80">SMTP Host *</Label>
              <Input value={settings.smtp_host} onChange={e => setSettings({ ...settings, smtp_host: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="smtp-host-input" placeholder="smtp.gmail.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Port *</Label>
              <Input type="number" value={settings.smtp_port} onChange={e => setSettings({ ...settings, smtp_port: Number(e.target.value) })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="smtp-port-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Security</Label>
              <Select value={settings.smtp_security} onValueChange={v => setSettings({ ...settings, smtp_security: v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="smtp-security-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="ssl" className="text-foreground">SSL (Port 465)</SelectItem>
                  <SelectItem value="tls" className="text-foreground">STARTTLS (Port 587)</SelectItem>
                  <SelectItem value="none" className="text-foreground">None (Port 25)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Email Address *</Label>
              <Input value={settings.smtp_email} onChange={e => setSettings({ ...settings, smtp_email: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="smtp-email-input" placeholder="you@company.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Password / App Password *</Label>
              <Input type="password" value={settings.app_password} onChange={e => { setSettings({ ...settings, app_password: e.target.value }); setPasswordChanged(true); }}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="smtp-password-input" placeholder={passwordChanged ? "Enter password" : "Saved (enter new to change)"} />
            </div>
          </div>

          {/* Sender Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Sender Name</Label>
              <Input value={settings.sender_name} onChange={e => setSettings({ ...settings, sender_name: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="sender-name-input" placeholder="Your Name / Company" />
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Company Name</Label>
              <Input value={settings.company_name} onChange={e => setSettings({ ...settings, company_name: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="company-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-card-foreground/80">Email Signature</Label>
            <textarea value={settings.email_signature} onChange={e => setSettings({ ...settings, email_signature: e.target.value })}
              className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[80px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              data-testid="email-signature-input" placeholder="Best regards,&#10;Your Name&#10;Company Name&#10;+91 XXXXX XXXXX" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="save-email-settings-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Settings
            </Button>
            <Button onClick={handleTest} disabled={testing} variant="outline" className="border-input text-card-foreground/80 gap-2" data-testid="test-email-button">
              {testing ? <Loader2 className="animate-spin" size={16} /> : <SendHorizonal size={16} />} Send Test Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Logs */}
      <Card className="bg-card border-border" data-testid="email-logs-card">
        <CardHeader>
          <CardTitle className="text-lg text-card-foreground">Email History</CardTitle>
          <CardDescription className="text-muted-foreground">Recent emails sent from the platform</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">To</TableHead>
                <TableHead className="text-muted-foreground">Subject</TableHead>
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No emails sent yet</TableCell></TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} className="border-border hover:bg-muted/50">
                  <TableCell className="text-foreground text-sm">{log.to_email}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[250px] truncate">{log.subject}</TableCell>
                  <TableCell className="text-muted-foreground text-xs capitalize">{log.email_type}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(log.sent_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
